import React from 'react';

import { UserRoles } from '../../data/enums';
import { PageHeader } from '../../components/ui';
import { useDashboardData } from './hooks/use-dashboard-data';
import DashboardAlerts from './components/dashboard-alerts';
import DashboardFilters from './components/dashboard-filters';
import KpiGrid from './components/kpi-grid';
import WeeklyChart from './components/weekly-chart';
import TopItemsPanel from './components/top-items-panel';
import OrderChannelsPanel from './components/order-channels-panel';
import DashboardSkeleton from './components/dashboard-skeleton';
import './dashboard.css';

const Dashboard = () => {
  const data = useDashboardData();

  const {
    isAdmin,
    userRoles,
    companyList,
    storeList,
    storeWithMissingSettings,
    bankAccount,
    globalCompany,
    setGlobalCompany,
    search,
    setSearch,
    selectedDateRange,
    setSelectedDateRange,
    isShowCustomRange,
    onDateChange,
    onDateRangeSelectChange,
    onSelectStore,
    weekRange,
    handleWeekChange,
    weekRangeData,
    weekRangeChartType,
    setWeekRangeChartType,
    chart,
    orderInformations,
    pieChart,
    orderedTop10Items,
    top10Type,
    setTop10Type,
    selectedTop10DateRange,
    onTop10DateRangeSelectChange,
    loading,
    dateOptions,
  } = data;

  const canSeeMetrics = userRoles.some((r) =>
    [
      UserRoles.ADMIN,
      UserRoles.STORE_OWNER,
      UserRoles.STORE_MANAGER,
      UserRoles.ACCOUNTANT,
    ].includes(r),
  );
  const canSeePanels = userRoles.some((r) =>
    [
      UserRoles.STORE_OWNER,
      UserRoles.STORE_MANAGER,
      UserRoles.ACCOUNTANT,
    ].includes(r),
  );

  if (loading.initialLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="dashboard-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <DashboardAlerts
          userRoles={userRoles}
          globalCompany={globalCompany}
          bankAccount={bankAccount}
          storeList={storeList}
          storeWithMissingSettings={storeWithMissingSettings}
        />

        <PageHeader
          title="Dashboard"
          description="Track your sales metrics and optimize your business performance."
        />

        {canSeeMetrics && (
          <div className="space-y-6">
            <div className="reveal">
              <DashboardFilters
                isAdmin={isAdmin}
                dateOptions={dateOptions}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
                isShowCustomRange={isShowCustomRange}
                search={search}
                onDateChange={onDateChange}
                onDateRangeSelectChange={onDateRangeSelectChange}
                companyList={companyList}
                globalCompany={globalCompany}
                setGlobalCompany={setGlobalCompany}
                setSearch={setSearch}
                storeList={storeList}
                onSelectStore={onSelectStore}
              />
            </div>

            <KpiGrid
              orderInformations={orderInformations}
              clickCountLoading={loading.clickCountLoading}
            />

            <WeeklyChart
              weekRangeChartType={weekRangeChartType}
              setWeekRangeChartType={setWeekRangeChartType}
              handleWeekChange={handleWeekChange}
              loading={loading.graphicLoading}
              weekRangeData={weekRangeData}
              chart={chart}
              weekRange={weekRange}
            />

            {canSeePanels && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <TopItemsPanel
                  items={orderedTop10Items}
                  top10Type={top10Type}
                  setTop10Type={setTop10Type}
                  selectedTop10DateRange={selectedTop10DateRange}
                  onTop10DateRangeSelectChange={onTop10DateRangeSelectChange}
                />
                <OrderChannelsPanel
                  orderInformations={orderInformations}
                  pieChart={pieChart}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
